import {
  HbrBanner,
  HbrButton,
  HbrDomTable,
  HbrIcon,
  HbrRadioButton,
  HbrSpinner,
  HbrViewSwitcher
} from '@harbor/elements-react-standalone';
import React, { useEffect, useState } from 'react';
import { Column, ColumnType } from '../../types/table';
import {
  ApprovalRequest,
  ApprovalRequestStatusLevel,
  ApprovalRequestType,
  ApprovalTemplate
} from '../../types/approval-request';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../support/store';
import { useFetchApprovalRequests } from '../../hooks/approval-request-hooks';
import cellTemplate from '../Common/TableView/cell-templates';
import { alertsInfoLarge, caretRight } from '@harbor/elements/icons';
import ApprovalsListDetail from './ApprovalsListDetail';
import ApprovalListItemActions from './ApprovalsListItemActions';
import { checkApprovalTemplatesLoaded } from '../../support/slices/approvalTemplatesSlice';

const statusNameMap = {
  UpdateRequired: 'Update Required',
}

const statusFormat = (value) => {
  const statusColorMap = {
    Cancelled: 'bg-gray-600',
    Pending: 'bg-warning',
    Approved: 'bg-success',
    'Update Required': 'bg-warning',
    UpdateRequired: 'bg-warning',
    Denied: 'bg-danger',
  };
  const formatted = statusNameMap[value] ?? value;
  return (
    <div className='flex items-center gap-2 font-semibold'>
      <div
        className={`${statusColorMap[value]} min-w-6 h-6 rounded-full`}
      ></div> 
      {formatted}
    </div>
  );
};

const ApprovalsListPage = () => {
  const [tableData, setTableData] = useState([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>('1');
  const isManagerView = useSelector(
    (state: RootState) => state.user.userData.isManager
  );
  const userId = useSelector(
    (state: RootState) => state.user.userData.id
  );

  const { navigationViews } = useSelector((state: RootState) => state.user);
  const isAdmin = (navigationViews ?? []).includes('admin');

  const { data: requestDetails, isLoading } = useFetchApprovalRequests({
    status: status ?? undefined,
    ...isAdmin
      ? {}
      : isManagerView
        ? { approver: userId?.toString() }
        : { requestor: userId?.toString() }
  });

  useEffect(() => {
    if (requestDetails) {
      setTableData(
        (requestDetails).map((responseItem: any) => ({
          id: responseItem.id.toString(),
          title: responseItem?.title ?? `Request #${responseItem.id}`,
          created: responseItem?.created ?? 'No creation date available',
          requestor: responseItem?.requestor?.email ?? '',
          approver: responseItem?.approver?.email ?? '',
          status: responseItem?.status?.title ?? '',
          type: responseItem?.requested_item_type?.toUpperCase() ?? '',
          templateFormatted: responseItem,
          expandable: true,
        }))
      );
    }
  }, [requestDetails]);

  const changeStatusFilter = (newStatus: string) => {
    if (status !== newStatus) {
      setStatus(newStatus);
      setExpanded([]);
    }
  }

  const columns: Column[] = [
    {
      id: 'expandable',
      minWidth: 36,
      name: '',
      columnType: ColumnType.Custom,
      renderCell: ({ data, column }) => {
        const id = 'expandable-btn-' + data.id;
        return data.expandable ? (
          <HbrButton
            key={id}
            variant='text'
            size='small'
            aria={{
              label: 'expandable row',
            }}
            onClick={() => {
              if (expanded.includes(data.id)) {
                setExpanded(expanded.filter((id) => id !== data.id));
              } else {
                setExpanded([...expanded, data.id]);
              }
            }}
          >
            <HbrIcon
              className={
                expanded.includes(data.id)
                  ? 'hbr-css__expandable-btn-expanded'
                  : ''
              }
              asset={caretRight}
            ></HbrIcon>
          </HbrButton>
        ) : null;
      },
    },
    { id: 'title', name: 'Name', columnType: ColumnType.TitleLink, minWidth: 320 },
    { id: 'type', name: 'Type', columnType: ColumnType.String },
    {
      id: 'status',
      name: 'Status',
      columnType: ColumnType.String,
      renderCell: ({ data }) => statusFormat(data.status),
    },
    {
      id: 'actions',
      name: '',
      columnType: ColumnType.Custom,
      renderCell: ({ data, column }) => <ApprovalListItemActions request={data.templateFormatted} managerView={isManagerView} />,
    },
    { id: 'created', name: 'Created', columnType: ColumnType.Date },
    { id: 'requestor', name: 'Requestor', columnType: ColumnType.String },
    { id: 'approver', name: 'Approver', columnType: ColumnType.String },
  ];

  const renderBody = () => {
    if (!tableData || tableData.length === 0) {
      return null;
    }
    const list = [];
    tableData.forEach((row) => {
      columns.forEach((column) => {
        list.push(cellTemplate(column, row, {
          titleLink: row?.templateFormatted?.requested_item?.opportunity
            ? `/opportunity/${row.templateFormatted.requested_item.opportunity}`
            : '/',
        }));
      });
      // expandable section
      if (expanded.includes(row.id)) {
        list.push(
          <div
            slot={`row-${row.id}-expandable`}
            key={`row-${row.id}-expandable`}
            className='border-l-8 border-info pb-4 pl-7 pr-2 bg-gray-100 relative'
          >
            <ApprovalsListDetail approval={row.templateFormatted} />
            {/* <pre className="text-xs">{JSON.stringify(row.templateFormatted, null, 2)}</pre> */}
          </div>
        );
      }
    });

    return list;
  };

  return (
    <section className='m-4'>
      <HbrViewSwitcher
        label='Approval Status'
        className='my-4'
        value={status}
        onHbr-change={e => changeStatusFilter(e.target.value)}
      >
        <HbrRadioButton size="medium" pill value={null}>All</HbrRadioButton>
        {[1,2,3,4,5].map(n => (
          <HbrRadioButton
            key={`approval-list-status-filter-${n}`}
            size='medium'
            pill
            value={String(n)}
          >
            {statusNameMap[ApprovalRequestStatusLevel[n]] ?? ApprovalRequestStatusLevel[n]}
          </HbrRadioButton>
        ))}
      </HbrViewSwitcher>
      
      {isLoading && <HbrSpinner size='large' />}
      {!isLoading && (
        <HbrDomTable
          autoLayout={true}
          columns={columns}
          data={tableData}
          expanded={expanded}
          density={'spacious'}
        >
          {isLoading && <HbrSpinner />}
          {!isLoading && !tableData?.length && (
            <HbrBanner sentiment='info' variant='fill' open closable={true}>
              <HbrIcon slot='icon' asset={alertsInfoLarge} />
              No approvals to show
            </HbrBanner>
          )}
          {!isLoading && tableData?.length && renderBody()}
        </HbrDomTable>
      )}
    </section>
  );
};

export default ApprovalsListPage;
